import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { useStream } from "@convex-dev/persistent-text-streaming/react";
import type { StreamId } from "@convex-dev/persistent-text-streaming";
import {
  Archive,
  BookOpen,
  Clock,
  Loader2,
  MessageCircle,
  Minus,
  Plus,
  SendHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { MarkdownBody } from "@/components/ui/markdown-body";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────────────────

type WidgetState = "open" | "minimized" | "closed";

type InternalSupportThread = {
  _id: string;
  _creationTime: number;
  internalUserId: string;
  agentId?: string;
  status: "open" | "closed";
  lastMessageAt: number;
  lastMessagePreview?: string;
  createdAt: number;
  updatedAt: number;
};

type InternalSupportMessage = {
  _id: string;
  _creationTime: number;
  threadId: string;
  senderId: string;
  senderType: "internal_user" | "agent";
  body: string;
  isStreaming?: boolean;
  streamId?: string;
  createdAt: number;
};

type ThreadWithMessages = {
  thread: InternalSupportThread;
  messages: InternalSupportMessage[];
  agent: { name: string } | null;
} | null;

// ─── Constants ───────────────────────────────────────────────────────

const WIDGET_STATE_KEY = "internal-support-widget-state";
const CURRENT_THREAD_KEY = "internal-support-current-thread";

const STREAM_URL = new URL(
  "/api/internal-support/stream",
  typeof window !== "undefined" ? window.location.origin : "http://localhost",
);

// ─── Main Widget ─────────────────────────────────────────────────────

export function InternalSupportChatWidget() {
  const [widgetState, setWidgetState] = React.useState<WidgetState>(() => {
    if (typeof window === "undefined") return "closed";
    return (localStorage.getItem(WIDGET_STATE_KEY) as WidgetState) ?? "closed";
  });

  const [currentThreadId, setCurrentThreadId] = React.useState<string | null>(
    () => {
      if (typeof window === "undefined") return null;
      return localStorage.getItem(CURRENT_THREAD_KEY);
    },
  );
  const [startingNewConversation, setStartingNewConversation] =
    React.useState(false);

  const [showThreadHistory, setShowThreadHistory] = React.useState(false);

  React.useEffect(() => {
    localStorage.setItem(WIDGET_STATE_KEY, widgetState);
  }, [widgetState]);

  React.useEffect(() => {
    if (currentThreadId) {
      localStorage.setItem(CURRENT_THREAD_KEY, currentThreadId);
    } else {
      localStorage.removeItem(CURRENT_THREAD_KEY);
    }
  }, [currentThreadId]);

  const handleOpen = React.useCallback(() => setWidgetState("open"), []);
  const handleMinimize = React.useCallback(
    () => setWidgetState("minimized"),
    [],
  );
  const handleClose = React.useCallback(() => {
    setWidgetState("closed");
    setShowThreadHistory(false);
  }, []);

  const handleNewConversation = React.useCallback(() => {
    setCurrentThreadId(null);
    setStartingNewConversation(true);
    setShowThreadHistory(false);
  }, []);

  const handleSelectThread = React.useCallback((threadId: string) => {
    setCurrentThreadId(threadId);
    setStartingNewConversation(false);
    setShowThreadHistory(false);
  }, []);

  const handleThreadCreated = React.useCallback((threadId: string) => {
    setCurrentThreadId(threadId);
    setStartingNewConversation(false);
  }, []);

  const handleThreadClosed = React.useCallback(
    (threadId: string) => {
      if (currentThreadId !== threadId) return;
      setCurrentThreadId(null);
      setStartingNewConversation(true);
    },
    [currentThreadId],
  );

  const handleThreadDeleted = React.useCallback(
    (threadId: string) => {
      if (currentThreadId !== threadId) return;
      setCurrentThreadId(null);
      setStartingNewConversation(true);
    },
    [currentThreadId],
  );

  if (widgetState === "closed" || widgetState === "minimized") {
    return <FloatingButton onClick={handleOpen} />;
  }

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col rounded-xl border border-border bg-background shadow-2xl"
      style={{ width: 400, height: 600 }}>
      <ChatHeader
        onMinimize={handleMinimize}
        onClose={handleClose}
        onNewConversation={handleNewConversation}
        onToggleHistory={() => setShowThreadHistory((v) => !v)}
        showThreadHistory={showThreadHistory}
      />
      {showThreadHistory ? (
        <ThreadHistory
          onSelectThread={handleSelectThread}
          currentThreadId={currentThreadId}
          onThreadClosed={handleThreadClosed}
          onThreadDeleted={handleThreadDeleted}
        />
      ) : (
        <ChatBody
          threadId={currentThreadId}
          startNewConversation={startingNewConversation}
          onThreadCreated={handleThreadCreated}
        />
      )}
    </div>
  );
}

// ─── Floating Button ─────────────────────────────────────────────────

function FloatingButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
      aria-label="Open Knowledge Assistant"
    >
      <MessageCircle className="size-6" />
    </button>
  );
}

// ─── Chat Header ─────────────────────────────────────────────────────

function ChatHeader({
  onMinimize,
  onClose,
  onNewConversation,
  onToggleHistory,
  showThreadHistory,
}: {
  onMinimize: () => void;
  onClose: () => void;
  onNewConversation: () => void;
  onToggleHistory: () => void;
  showThreadHistory: boolean;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between rounded-t-xl border-b bg-primary px-4 py-3">
      <div className="flex items-center gap-2">
        <BookOpen className="size-4 text-primary-foreground/80" />
        <span className="text-sm font-semibold text-primary-foreground">
          Knowledge Assistant
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onToggleHistory}
          className="rounded-md p-1 text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          aria-label={showThreadHistory ? "Back to chat" : "Thread history"}
          title={showThreadHistory ? "Back to chat" : "Thread history"}
        >
          <Clock className="size-4" />
        </button>
        <button
          onClick={onNewConversation}
          className="rounded-md p-1 text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          aria-label="New conversation"
          title="New conversation"
        >
          <Plus className="size-4" />
        </button>
        <button
          onClick={onMinimize}
          className="rounded-md p-1 text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          aria-label="Minimize"
        >
          <Minus className="size-4" />
        </button>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          aria-label="Close"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Chat Body ───────────────────────────────────────────────────────

function ChatBody({
  threadId,
  startNewConversation,
  onThreadCreated,
}: {
  threadId: string | null;
  startNewConversation: boolean;
  onThreadCreated: (id: string) => void;
}) {
  const threadData = useQuery(
    api.internalSupport.getMyInternalThreadWithMessages,
    startNewConversation ? "skip" : threadId ? { threadId } : {},
  ) as ThreadWithMessages | undefined;

  const sendMessage = useMutation(
    api.internalSupport.sendInternalSupportMessage,
  );

  const [isSending, setIsSending] = React.useState(false);

  const handleSend = React.useCallback(
    async (body: string) => {
      setIsSending(true);
      try {
        const currentThreadId =
          threadId ?? (startNewConversation ? undefined : threadData?.thread?._id);
        const result = await sendMessage({
          threadId: currentThreadId ?? undefined,
          body,
        });
        if (result?.threadId && !threadId) {
          onThreadCreated(result.threadId);
        }
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to send message",
        );
      } finally {
        setIsSending(false);
      }
    },
    [sendMessage, startNewConversation, threadData?.thread?._id, threadId, onThreadCreated],
  );

  const messages = startNewConversation ? [] : threadData?.messages ?? [];

  return (
    <>
      <MessageList messages={messages} />
      <ChatInput onSend={handleSend} disabled={isSending} />
    </>
  );
}

// ─── Message List ────────────────────────────────────────────────────

function MessageList({ messages }: { messages: InternalSupportMessage[] }) {
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isUserScrolled, setIsUserScrolled] = React.useState(false);

  React.useEffect(() => {
    if (!isUserScrolled) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, isUserScrolled]);

  const handleScroll = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setIsUserScrolled(!isAtBottom);
  }, []);

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <BookOpen className="size-10 text-muted-foreground/40" />
        <div>
          <p className="text-sm font-medium text-foreground">
            Knowledge Assistant
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Ask me anything about company documents, policies, and procedures.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-3"
      onScroll={handleScroll}
    >
      <div className="flex flex-col gap-3">
        {messages.map((msg) => (
          <ChatMessage key={msg._id} message={msg} />
        ))}
      </div>
      <div ref={bottomRef} />
    </div>
  );
}

// ─── Chat Message ────────────────────────────────────────────────────

function ChatMessage({ message }: { message: InternalSupportMessage }) {
  const isUser = message.senderType === "internal_user";
  const isStreaming = message.isStreaming && message.streamId;

  return (
    <div
      className={cn("flex", isUser ? "justify-end" : "justify-start")}
    >
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-3 py-2 text-sm",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        {isStreaming && message.streamId ? (
          <StreamingMessageBody
            streamId={message.streamId}
            fallback={message.body}
          />
        ) : message.body ? (
          <MessageContent body={message.body} isUser={isUser} />
        ) : (
          <TypingIndicator />
        )}
      </div>
    </div>
  );
}

// ─── Message Content with Citations ──────────────────────────────────

function MessageContent({
  body,
  isUser,
}: {
  body: string;
  isUser: boolean;
}) {
  if (isUser) {
    return <span className="whitespace-pre-wrap">{body}</span>;
  }

  return (
    <div>
      <MarkdownBody content={body} />
      <DocumentCitations body={body} />
    </div>
  );
}

// ─── Document Citations ──────────────────────────────────────────────

function DocumentCitations({ body }: { body: string }) {
  // Extract document references from agent responses
  // The agent cites documents by title in its response, and the knowledge base
  // search returns slugs. We look for markdown links pointing to knowledgebase.
  const kbLinkPattern = /\[([^\]]+)\]\(\/admin\/knowledgebase\/doc\/([^)]+)\)/g;
  const citations: Array<{ title: string; slug: string }> = [];
  let match: RegExpExecArray | null;

  while ((match = kbLinkPattern.exec(body)) !== null) {
    if (match[1] && match[2]) {
      citations.push({ title: match[1], slug: match[2] });
    }
  }

  if (citations.length === 0) return null;

  return (
    <div className="mt-2 border-t border-border/50 pt-2">
      <p className="mb-1 text-xs font-medium text-muted-foreground">Sources</p>
      <div className="flex flex-col gap-1">
        {citations.map((c) => (
          <a
            key={c.slug}
            href={`/admin/knowledgebase/doc/${c.slug}`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            title={c.title}
          >
            <BookOpen className="size-3 shrink-0" />
            {c.title}
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── Streaming Message ───────────────────────────────────────────────

function StreamingMessageBody({
  streamId,
  fallback,
}: {
  streamId: string;
  fallback: string;
}) {
  const [authToken, setAuthToken] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const { data } = await authClient.convex.token();
        if (mounted) setAuthToken(data?.token ?? null);
      } catch {
        if (mounted) setAuthToken(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const driven = Boolean(authToken);
  const { text, status } = useStream(
    api.internalSupportStreaming.getInternalStreamBody,
    STREAM_URL,
    driven,
    streamId as StreamId,
    { authToken },
  );

  const displayText = text || fallback;

  if (status === "pending" && !displayText) {
    return <TypingIndicator />;
  }

  if ((status === "error" || status === "timeout") && !displayText) {
    return <TypingIndicator label="Still working..." />;
  }

  return (
    <div>
      <MarkdownBody content={displayText} />
      {status === "streaming" && (
        <span className="ml-1 inline-block size-1.5 animate-pulse rounded-full bg-primary" />
      )}
      {displayText && <DocumentCitations body={displayText} />}
    </div>
  );
}

// ─── Typing Indicator ────────────────────────────────────────────────

function TypingIndicator({ label }: { label?: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 text-muted-foreground">
      <Loader2 className="size-3.5 animate-spin" />
      <span className="text-xs">{label ?? "Thinking..."}</span>
    </div>
  );
}

// ─── Chat Input ──────────────────────────────────────────────────────

function ChatInput({
  onSend,
  disabled,
}: {
  onSend: (body: string) => void;
  disabled: boolean;
}) {
  const [value, setValue] = React.useState("");
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleSubmit = React.useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleInput = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
      const el = e.target;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
    },
    [],
  );

  return (
    <div className="shrink-0 border-t p-3">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question..."
          disabled={disabled}
          className="flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/50 disabled:opacity-50"
          rows={1}
          style={{ maxHeight: 100 }}
        />
        <Button
          size="icon"
          onClick={handleSubmit}
          disabled={disabled || !value.trim()}
          className="shrink-0"
          aria-label="Send message"
        >
          <SendHorizontal className="size-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Thread History ──────────────────────────────────────────────────

function ThreadHistory({
  onSelectThread,
  currentThreadId,
  onThreadClosed,
  onThreadDeleted,
}: {
  onSelectThread: (threadId: string) => void;
  currentThreadId: string | null;
  onThreadClosed: (threadId: string) => void;
  onThreadDeleted: (threadId: string) => void;
}) {
  const threads = useQuery(api.internalSupport.listMyInternalThreads, {
    limit: 20,
  }) as InternalSupportThread[] | undefined;
  const closeThread = useMutation(api.internalSupport.closeInternalThread);
  const deleteThread = useMutation(api.internalSupport.deleteInternalThread);
  const [closingThreadId, setClosingThreadId] = React.useState<string | null>(null);
  const [deletingThreadId, setDeletingThreadId] = React.useState<string | null>(
    null,
  );

  const handleCloseThread = React.useCallback(
    async (event: React.MouseEvent, threadId: string) => {
      event.stopPropagation();
      if (closingThreadId === threadId) return;
      try {
        setClosingThreadId(threadId);
        await closeThread({ threadId: threadId as any });
        onThreadClosed(threadId);
        toast.success("Conversation closed");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to close conversation",
        );
      } finally {
        setClosingThreadId(null);
      }
    },
    [closeThread, closingThreadId, onThreadClosed],
  );

  const handleDeleteThread = React.useCallback(
    async (event: React.MouseEvent, threadId: string) => {
      event.stopPropagation();
      if (deletingThreadId === threadId) return;
      if (
        !window.confirm(
          "Delete this conversation and all its messages? This cannot be undone.",
        )
      ) {
        return;
      }
      try {
        setDeletingThreadId(threadId);
        await deleteThread({ threadId: threadId as any });
        onThreadDeleted(threadId);
        toast.success("Conversation deleted");
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to delete conversation",
        );
      } finally {
        setDeletingThreadId(null);
      }
    },
    [deleteThread, deletingThreadId, onThreadDeleted],
  );

  const handleThreadRowKeyDown = React.useCallback(
    (event: React.KeyboardEvent, threadId: string) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onSelectThread(threadId);
    },
    [onSelectThread],
  );

  if (threads === undefined) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
        <Clock className="size-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">No previous conversations</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {threads.map((thread) => (
        <div
          key={thread._id}
          onClick={() => onSelectThread(thread._id)}
          onKeyDown={(event) => handleThreadRowKeyDown(event, thread._id)}
          role="button"
          tabIndex={0}
          className={cn(
            "flex w-full flex-col gap-1 border-b px-4 py-3 text-left transition-colors hover:bg-muted/50",
            thread._id === currentThreadId && "bg-muted/30",
          )}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {formatDate(thread.lastMessageAt)}
            </span>
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                  thread.status === "open"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
                )}
              >
                {thread.status}
              </span>
              {thread.status === "open" ? (
                <button
                  type="button"
                  className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={(event) => void handleCloseThread(event, thread._id)}
                  aria-label="Close conversation"
                  title="Close conversation"
                  disabled={closingThreadId === thread._id}
                >
                  {closingThreadId === thread._id ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Archive className="size-3" />
                  )}
                </button>
              ) : null}
              <button
                type="button"
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                onClick={(event) => void handleDeleteThread(event, thread._id)}
                aria-label="Delete conversation"
                title="Delete conversation"
                disabled={deletingThreadId === thread._id}
              >
                {deletingThreadId === thread._id ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Trash2 className="size-3" />
                )}
              </button>
            </div>
          </div>
          <p className="line-clamp-2 text-sm text-foreground">
            {thread.lastMessagePreview ?? "New conversation"}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────

function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}
